import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { AstraEffort } from "../lib/delegatePresentation";

const choices = [{ value: "high", label: "High" }, { value: "xhigh", label: "xHigh" },
  { value: "max", label: "Max" }] as const;

/** Draft-only setup. Preview locally; only the tick edits the scoped command. */
export default function DelegateEffortPicker({ id, value, disabled, requestOpen = 0,
  onChange, onDismiss, "aria-label": label = "Astra main worker effort" }: {
  id: string; value: AstraEffort; disabled?: boolean; requestOpen?: number;
  onChange(value: AstraEffort): void; onDismiss?(): void; "aria-label"?: string;
}) {
  const [open, setOpen] = useState(Boolean(requestOpen && !disabled));
  const [preview, setPreview] = useState<AstraEffort>(value);
  const [lastInputs, setLastInputs] = useState({ requestOpen, disabled });
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const slider = useRef<HTMLInputElement>(null);
  const selected = choices.findIndex(choice => choice.value === preview);
  const openPicker = () => { if (!disabled) { setPreview(value); setOpen(true); } };
  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => { if (onDismiss) onDismiss(); else trigger.current?.focus(); });
  };

  // Adjust this component's state before committing children, not in a second
  // effect render. Ordinary parent/value rerenders never discard a preview.
  if (lastInputs.requestOpen !== requestOpen || lastInputs.disabled !== disabled) {
    setLastInputs({ requestOpen, disabled });
    if (disabled) setOpen(false);
    else if (requestOpen && lastInputs.requestOpen !== requestOpen) { setPreview(value); setOpen(true); }
  }
  useEffect(() => {
    if (!open) return;
    const panel = dialog.current;
    if (!panel) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.showModal(); // Native focus containment/inert background; outside the composer form.
    slider.current?.focus({ preventScroll: true });
    return () => { panel.close(); document.body.style.overflow = overflow; };
  }, [open]);

  return <>
    <button ref={trigger} id={id} type="button" className="delegateBadge__effort"
      aria-label={label} aria-haspopup="dialog" aria-expanded={open}
      disabled={disabled} onClick={openPicker}>
      <span>Astra</span><span className="delegateBadge__separator" aria-hidden="true">·</span>
      <span>{choices.find(choice => choice.value === value)?.label ?? "Max"}</span>
      <svg className="delegateEffortPicker__chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m4 6 4 4 4-4" /></svg>
    </button>
    {open && createPortal(<dialog ref={dialog} className="astraSheet" aria-labelledby={id + "-title"}
      aria-describedby={id + "-hint"} data-effort={preview}
      onCancel={event => { event.preventDefault(); close(); }}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key !== "Tab") return;
        const controls = event.currentTarget.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)");
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}
      onClick={event => { if (event.target === event.currentTarget) close(); }}>
      <div className="astraSheet__panel">
        <header className="astraSheet__head">
          <button type="button" className="astraSheet__cancel" aria-label="Cancel Astra setup" onClick={close}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
          </button>
          <h2 id={id + "-title"}>GPT-6 Astra</h2>
          <button type="button" className="astraSheet__confirm" aria-label="Confirm Astra effort"
            onClick={() => { onChange(preview); close(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
          </button>
        </header>
        <div className="astraSheet__body">
          <div className="astraSheet__readout"><strong>{choices[selected].label}</strong><span>Thinking level</span></div>
          <div className="astraSheet__slider" style={{ "--astra-progress": selected / 2 } as CSSProperties}>
            <span className="astraSheet__fill" aria-hidden="true" />
            <span className="astraSheet__dots" aria-hidden="true"><i /><i /><i /></span>
            <input ref={slider} type="range" min="0" max="2" step="1" value={selected}
              aria-label="Astra thinking level" aria-valuetext={choices[selected].label}
              onChange={event => setPreview(choices[Number(event.currentTarget.value)].value)} />
          </div>
          <div className="astraSheet__levels" role="group" aria-label="Thinking levels">
            {choices.map(choice => <button key={choice.value} type="button" aria-pressed={preview === choice.value}
              onClick={() => setPreview(choice.value)}>{choice.label}</button>)}
          </div>
          <p id={id + "-hint"}>For this message. Claude’s setting stays unchanged.</p>
        </div>
      </div>
    </dialog>, document.body)}
  </>;
}
